import PropTypes from 'prop-types';
import Entity from '@/common-utils/components/renders/entity/Entity';
import { Separator } from '@/common-utils/components/ui/separator';
import { nsCustomization } from '@/app/i18n/namespaces';

const ENTITY_PREFIX = 'details.monetic_settings.gateway';

const formatEndpoint = (ip, port) => {
    if (!ip) {
        return null;
    }

    return port !== null && port !== undefined ? `${ip}:${port}` : ip;
};

/**
 * Read-only gateways display for Payludd terminal state.
 * @param {object} root0 component props
 * @param {Array<object>} root0.gateways gateways from terminal state
 * @returns {JSX.Element|null} gateways section
 */
function GatewayView({ gateways }) {
    if (!gateways.length) {
        return null;
    }

    return (
        <div>
            <Entity
                className='text-sm font-semibold opacity-[.8]'
                entity={`${ENTITY_PREFIX}.title`}
                namespace={nsCustomization}
            />
            <Separator className='my-2' />
            <div className='flex flex-col gap-3 sm:flex-row sm:flex-wrap mt-3'>
                {gateways.map((gateway) => {
                    const mainAddress = formatEndpoint(gateway.mainIp, gateway.mainPort);
                    const backupAddress = formatEndpoint(gateway.backupIp, gateway.backupPort);

                    return (
                        <div
                            key={gateway.id || gateway.name}
                            className='w-full sm:w-[320px] border border-secondary-hover rounded-md bg-muted/30 overflow-hidden shrink-0'
                        >
                            <div className='px-3 py-2 border-b bg-secondary-hover'>
                                <span className='text-sm font-semibold text-dark-foreground'>
                                    {gateway.name || gateway.id}
                                </span>
                            </div>
                            <div className='flex flex-col gap-3 px-3 py-4'>
                                <div className='flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2'>
                                    <Entity
                                        className='text-sm text-labelGray-foreground shrink-0 sm:w-[8.5rem]'
                                        entity={`${ENTITY_PREFIX}.main_address`}
                                        namespace={nsCustomization}
                                    />
                                    <span className='text-sm font-semibold break-all sm:flex-1 sm:text-right sm:whitespace-nowrap'>
                                        {mainAddress}
                                    </span>
                                </div>
                                <div className='flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2'>
                                    <Entity
                                        className='text-sm text-labelGray-foreground shrink-0 sm:w-[8.5rem]'
                                        entity={`${ENTITY_PREFIX}.backup`}
                                        namespace={nsCustomization}
                                    />
                                    {backupAddress
                                        ? (
                                            <span className='text-sm font-semibold break-all sm:flex-1 sm:text-right sm:whitespace-nowrap'>
                                                {backupAddress}
                                            </span>
                                        )
                                        : (
                                            <Entity
                                                className='text-sm font-semibold sm:flex-1 sm:text-right sm:whitespace-nowrap'
                                                entity={`${ENTITY_PREFIX}.none`}
                                                namespace={nsCustomization}
                                            />
                                        )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

GatewayView.propTypes = {
    gateways: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
        mainIp: PropTypes.string.isRequired,
        mainPort: PropTypes.number.isRequired,
        backupIp: PropTypes.string,
        backupPort: PropTypes.number
    })).isRequired
};

export default GatewayView;
